import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(isSameOrBefore);

const tagColors = [
  "#5cdbd3",
  "orange",
  "lime",
  "#ff7875",
  "geekblue",
  "gold",
  "success",
  "#52c41a",
  "cyan",
  "#fa541c",
  "#ffc53d",
  "#40a9ff",
  "#108ee9",
  "purple",
  "#73d13d",
  "#87d068",
  "#2db7f5",
  "processing",
  "#faad14",
  "#13c2c2",
  "magenta",
  "blue",
  "warning",
  "default",
  "#ff85c0",
  "#b37feb",
  "#1890ff",
  "volcano",
  "green",
  "red",
  "error",
  "#f50",
  "#597ef7",
  "#eb2f96",
  "#722ed1",
];

export function getColorFromString(
  input: string | null | undefined,
  colors?: string[],
): string {
  const s = input ?? "";
  let hash = 111;

  for (let i = 0; i < s.length; i++) {
    hash = s.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash;
  }

  const index = Math.abs(hash) % (colors?.length || tagColors.length);
  return colors?.[index] || tagColors[index];
}
export const uuidV4Regex =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const removeFields = <T>(
  data: any[],
  fields: string[],
  editedData: Record<string, Partial<T>>,
) => {
  return data.reduce<any[]>((acc, item) => {
    if (Object.keys(editedData).includes(item.id)) {
      const newItem = { ...item };
      fields.forEach((field) => {
        delete newItem[field];
      });
      acc.push(newItem);
    }
    return acc;
  }, []);
};

export const updatePath = (url: string, newValue: string) => {
  return url.split("/").slice(0, 3).join("/").concat(newValue);
};
export const nullOrUndefiend = (type: "new" | "update") => {
  return type === "new" ? undefined : null;
};

export type HttpMutationMethod = "POST" | "PATCH";

const clearedFieldForHttpMethod = (
  method: HttpMutationMethod,
): undefined | null => (method === "POST" ? undefined : null);

/**
 * Cleared inputs often submit `""`. Use `undefined` on POST so fields are omitted;
 * use `null` on PATCH so nullable fields are explicitly cleared server-side.
 */
export function emptyStringForHttpMethod<T>(
  value: T,
  method: HttpMutationMethod,
): T | undefined | null {
  if (value === "") {
    return clearedFieldForHttpMethod(method);
  }
  return value;
}

/**
 * Optional numeric form values: `""` (and null/undefined) use the same POST vs PATCH
 * semantics as {@link emptyStringForHttpMethod}; otherwise coerced with NaN
 * treated as absent/cleared the same way.
 */
export function normalizedNumber(
  value: unknown,
  method: HttpMutationMethod,
): number | undefined | null {
  if (value === "") {
    return clearedFieldForHttpMethod(method);
  }
  if (value === null || value === undefined) {
    return clearedFieldForHttpMethod(method);
  }
  const n = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(n)) {
    return clearedFieldForHttpMethod(method);
  }
  return n;
}
/**
 * Optional text form values: `""`, null, undefined, or whitespace-only strings
 * use POST vs PATCH cleared semantics; otherwise returns a trimmed string.
 */
export function normalizedString(
  value: unknown,
  method: HttpMutationMethod,
): string | undefined | null {
  if (value === "" || value === null || value === undefined) {
    return clearedFieldForHttpMethod(method);
  }
  const s = typeof value === "string" ? value : String(value);
  const trimmed = s.trim();
  if (trimmed === "") {
    return clearedFieldForHttpMethod(method);
  }
  return trimmed;
}
export const decimalYearsToYearsMonths = (decimalYears: number) => {
  const years = Math.floor(decimalYears);
  const months = Math.round((decimalYears - years) * 12);
  return `${years} years and ${months} months`;
};

/** Trim and validate DOB; returns a string dayjs can parse for age, or null. */
export function normalizeDateOfBirthForAge(
  value: string | null | undefined,
): string | null {
  if (value == null || value === "") return null;
  const trimmed = typeof value === "string" ? value.trim() : String(value);
  if (!trimmed) return null;
  const d = dayjs(trimmed);
  return d.isValid() ? trimmed : null;
}

/**
 * Age in fractional years from a normalized DOB (see {@link normalizeDateOfBirthForAge}).
 * Returns null for invalid or future dates of birth.
 */
export function getDecimalAgeYearsFromDateOfBirth(
  normalizedDateOfBirth: string,
): number | null {
  const birth = dayjs(normalizedDateOfBirth);
  if (!birth.isValid()) return null;
  const now = dayjs();
  if (birth.isAfter(now, "day")) return null;
  const years = now.diff(birth, "year", true);
  return Number.isFinite(years) ? years : null;
}

export const calculateMemberDiscount = ({
  discountPercentage,
  discountExpiryDate,
  total,
}: {
  discountPercentage?: number | string | null;
  discountExpiryDate?: string | null;
  total: number;
}) => {
  try {
    const parsedDiscountPercentage =
      typeof discountPercentage === "number"
        ? discountPercentage
        : typeof discountPercentage === "string"
        ? Number.parseFloat(discountPercentage)
        : NaN;

    if (
      !Number.isFinite(parsedDiscountPercentage) ||
      parsedDiscountPercentage <= 0
    ) {
      return 0;
    }
    if (!discountExpiryDate || typeof discountExpiryDate !== "string") {
      return 0;
    }

    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    const expiryDate = new Date(discountExpiryDate);
    if (Number.isNaN(expiryDate.getTime())) {
      return 0;
    }
    expiryDate.setHours(0, 0, 0, 0);

    if (currentDate.getTime() <= expiryDate.getTime()) {
      return (total * parsedDiscountPercentage) / 100;
    }
    return 0;
  } catch {
    return 0;
  }
};

export const handleSortOrder = (sortOrder: "asc" | "desc" | "") => {
  if (sortOrder === "") {
    return "asc";
  }
  return sortOrder === "asc" ? "desc" : "asc";
};

export const formatSnakeCaseLabel = (value?: string | null, fallback = "-") => {
  if (!value) return fallback;
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const sanitizeFormValues = <T extends Record<string, unknown>>(
  obj: T,
): T => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        return [key, undefined];
      }
      if (
        typeof value === "string" &&
        value.trim() !== "" &&
        !isNaN(Number(value))
      ) {
        return [key, Number(value)];
      }
      return [key, value];
    }),
  ) as T;
};

export const undefinedToNull = <T>(value: T | undefined, fallback: T): T =>
  value !== undefined ? value : fallback;

export const normalizeOptionalText = (value: string) => {
  if (value === "") return null;
  return value;
};

/**
 * List/table rows sometimes store a label as a string or as `{ name: string }`.
 * Use before rendering into React text nodes.
 */
export const displayNamedOrPrimitive = (
  value: unknown,
  fallback = "--",
): string | number => {
  if (value === null || value === undefined || value === "") return fallback;
  const t = typeof value;
  if (t === "string" || t === "number") return value as string | number;
  if (t === "boolean") return String(value);
  if (t === "object" && "name" in (value as object)) {
    const n = (value as { name: unknown }).name;
    if (typeof n === "string" || typeof n === "number") return n;
  }
  return fallback;
};

type CaseInput = string | Record<string, any> | any[];
export function toSnakeCase<T>(input: CaseInput): any {
  if (typeof input === 'string') {
    return input.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
  }

  // Handle array input
  if (Array.isArray(input)) {
    return input.map((item) => (typeof item === 'object' ? toSnakeCase(item) : item));
  }

  // Handle object input
  if (input !== null && typeof input === 'object') {
    const mapped: Record<string, any> = {};
    for (const key in input) {
      if (input.hasOwnProperty(key)) {
        const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
        const value = input[key];
        mapped[snakeKey] = typeof value === 'object' ? toSnakeCase(value) : value;
      }
    }
    return mapped;
  }

  return input;
}

export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
}